/**
 * Popover Component
 * Popover components based on Radix UI Popover primitive.
 * @packageDocumentation
 */

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { forwardRef, type ComponentRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/utils';

/** Popover root component. */
const Popover = PopoverPrimitive.Root;

/** Popover trigger component. */
const PopoverTrigger = PopoverPrimitive.Trigger;

/** Popover anchor component. */
const PopoverAnchor = PopoverPrimitive.Anchor;

/** Popover content component with styling. */
const PopoverContent = forwardRef<
  ComponentRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, sideOffset = 4, align = 'center', ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-md border border-border/60 bg-popover/95 p-4 text-popover-foreground shadow-lg backdrop-blur-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
